'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { setGlobalShowAlert } from '../utils/globalAlert';
import { CustomAlertDialog } from '@/components/custom-ui/alert-dialog/CustomAlertDialog';

type AlertDialogContent = {
	variant?: 'default' | 'error';
	logo?: boolean;
	title?: string;
	description?: string;
	cancelText?: string;
	actionText?: string;
	onCancel?: () => void;
	onConfirm?: () => void;
	hasSecondaryModal?: boolean;
};

type AlertDialogContextType = {
	showAlert: (content: Partial<AlertDialogContent>) => void;
	closeAlert: () => void;
};

const AlertDialogContext = createContext<AlertDialogContextType | undefined>(
	undefined
);

export function AlertDialogProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const [open, setOpen] = useState<boolean>(false);
	const [content, setContent] = useState<AlertDialogContent>({
		variant: 'default', // 기본값
		logo: false, // 기본값
		title: '',
		description: '',
		cancelText: 'Confirm', // 기본값
		actionText: '', // 기본은 action 버튼 없음
		onCancel: undefined,
		onConfirm: undefined,
		hasSecondaryModal: false, // 기본값
	});

	const showAlert = (contentInput: Partial<AlertDialogContent>) => {
		setContent({
			variant: contentInput.variant ?? 'default',
			logo: contentInput.logo ?? false,
			title: contentInput.title ?? '',
			description: contentInput.description ?? '',
			cancelText: contentInput.cancelText ?? '',
			actionText: contentInput.actionText ?? 'Confirm',
			onCancel: contentInput.onCancel,
			onConfirm: contentInput.onConfirm,
			hasSecondaryModal: contentInput.hasSecondaryModal ?? false,
		});
		setOpen(true);
	};

	const closeAlert = () => {
		setOpen(false);
	};

	useEffect(() => {
		setGlobalShowAlert(showAlert);
	}, []);

	return (
		<AlertDialogContext.Provider value={{ showAlert, closeAlert }}>
			{children}

			<CustomAlertDialog
				open={open}
				onOpenChange={open => {
					if (!open) closeAlert();
				}}
				variant={content.variant}
				logo={content.logo}
				title={content.title}
				description={content.description}
				cancelText={content.cancelText}
				actionText={content.actionText}
				onCancel={content.onCancel}
				onConfirm={content.onConfirm}
				hasSecondaryModal={content.hasSecondaryModal}
			/>
		</AlertDialogContext.Provider>
	);
}

export function useAlertDialog() {
	const context = useContext(AlertDialogContext);
	if (!context) {
		throw new Error('useAlertDialog must be used within AlertDialogProvider');
	}
	return context;
}
